import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users, HeartHandshake, HandHeart, Clock3, NotebookPen } from "lucide-react";

import { useSearchData } from "@/features/search/hooks";
import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { saveRecentSearch, groupRecentsByDate } from "@/lib/recent-searches";
import SearchResults, {
  buildSections,
  INITIAL_SHOW,
  SectionLabel,
  ResultCard,
  ResultRow,
} from "./SearchResults";

/* ─────────────────────────────────────────
   QUICK ACTIONS — chips shown when no query
───────────────────────────────────────── */
const QUICK_ACTIONS = [
  { label: "Nueva visita",  Icon: HeartHandshake, route: "/visits/new" },
  { label: "Nuevo hogar",   Icon: Users,          route: "/families/new" },
  { label: "Nueva oración", Icon: HandHeart,      route: "/prayers/new" },
  { label: "Nueva nota",    Icon: NotebookPen,    route: "/notes" },
];

/* ─────────────────────────────────────────
   MODAL
───────────────────────────────────────── */
export default function GlobalSearchModal({ open, onClose }) {
  const nav = useNavigate();
  const { districtId } = useDistrictGuard();

  const inputRef     = useRef(null);
  const scrollRef    = useRef(null);
  const itemRefs     = useRef({});
  const allItemsRef  = useRef([]);
  const selectedRef  = useRef(-1);
  const touchStartY  = useRef(null);

  const [localQuery,       setLocalQuery]       = useState("");
  const [selectedIndex,    setSelectedIndex]    = useState(-1);
  const [activeTab,        setActiveTab]        = useState("todo");
  const [expandedSections, setExpandedSections] = useState(new Set());

  const {
    query, setQuery, loading, indexState, recent,
    filteredPeople, filteredFamilies, filteredChurches,
    filteredVisits, filteredEvents, filteredPrayers, filteredNotes,
    filteredPreaching, filteredMaterials, filteredBaptisms,
    matchedCommands, totalResults,
  } = useSearchData({ districtId });

  /* ── umbral de 3 caracteres (capa visual, consistente con el inline) ── */
  const qReady     = query.trim().length >= 3;       // debounced — resultados/empty
  const localReady = localQuery.trim().length >= 3;  // inmediato — UI de la barra

  /* ── debounce ── */
  useEffect(() => {
    const t = setTimeout(() => setQuery(localQuery), 220);
    return () => clearTimeout(t);
  }, [localQuery, setQuery]);

  /* ── open/close ── */
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setLocalQuery("");
      setQuery("");
      setSelectedIndex(-1);
      setActiveTab("todo");
      setExpandedSections(new Set());
      scrollRef.current?.scrollTo(0, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── reset on new query ── */
  useEffect(() => {
    setSelectedIndex(-1);
    setActiveTab("todo");
    setExpandedSections(new Set());
    itemRefs.current = {};
  }, [query]);

  /* ── body scroll lock — restore previous value so concurrent sheets aren't unlocked ── */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /* ── sections ── */
  const sections = useMemo(
    () => qReady
      ? buildSections({ filteredPeople, filteredFamilies, filteredChurches, filteredVisits, filteredPrayers, filteredEvents, filteredNotes, filteredPreaching, filteredMaterials, filteredBaptisms })
      : [],
    [qReady, filteredPeople, filteredFamilies, filteredChurches, filteredVisits, filteredPrayers, filteredEvents, filteredNotes, filteredPreaching, filteredMaterials, filteredBaptisms]
  );

  const hasResults = sections.length > 0 || matchedCommands.length > 0;

  /* ── filter tabs (only when ≥2 non-"todo" sections) ── */
  const computedTabs = useMemo(() => {
    if (!qReady || !hasResults) return [];
    const tabs = [];
    const total = totalResults + matchedCommands.length;
    tabs.push({ id: "todo", label: "Todo", count: total });
    sections.forEach((s) => tabs.push({ id: s.key, label: s.label, count: s.items.length }));
    if (matchedCommands.length > 0)
      tabs.push({ id: "comandos", label: "Comandos", count: matchedCommands.length });
    return tabs;
  }, [qReady, hasResults, sections, matchedCommands, totalResults]);

  const showTabs = computedTabs.length > 2; // "Todo" + at least 2 others

  /* ── visible sections by tab ── */
  const visibleSections = useMemo(() => {
    if (!activeTab || activeTab === "todo" || activeTab === "comandos") return sections;
    return sections.filter((s) => s.key === activeTab);
  }, [sections, activeTab]);

  const showCommands = !activeTab || activeTab === "todo" || activeTab === "comandos";

  /* ── flat items for keyboard nav (only rendered items) ── */
  const allItems = useMemo(() => {
    const items = [];
    if (showCommands) {
      matchedCommands.forEach((cmd) => items.push({ route: cmd.route, recent: null }));
    }
    visibleSections.forEach((s) => {
      const visible = expandedSections.has(s.key)
        ? s.items
        : s.items.slice(0, INITIAL_SHOW);
      visible.forEach((item) => items.push(item));
    });
    return items;
  }, [matchedCommands, visibleSections, showCommands, expandedSections]);

  useEffect(() => { allItemsRef.current = allItems; }, [allItems]);
  useEffect(() => { selectedRef.current = selectedIndex; }, [selectedIndex]);

  /* ── scroll selected into view ── */
  useEffect(() => {
    if (selectedIndex >= 0) {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  /* ── navigate ── */
  const handleNavigate = useCallback((route, recentItem) => {
    if (recentItem) saveRecentSearch(recentItem);
    onClose();
    nav(route);
  }, [nav, onClose]);

  /* ── keyboard ── */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const items = allItemsRef.current;
      const idx   = selectedRef.current;
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((p) => Math.min(p + 1, items.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((p) => {
          const next = Math.max(p - 1, -1);
          if (next === -1) inputRef.current?.focus();
          return next;
        });
        return;
      }
      if (e.key === "Enter") {
        const target = idx >= 0 ? items[idx] : items[0];
        if (target) handleNavigate(target.route, target.recent ?? null);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, handleNavigate]);

  /* ── swipe down ── */
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e) => {
    if (touchStartY.current === null) return;
    const delta = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (delta > 80 && (scrollRef.current?.scrollTop ?? 0) <= 0) onClose();
  }, [onClose]);

  /* ── recents grouped ── */
  const recentGroups = useMemo(() => groupRecentsByDate(recent.slice(0, 10)), [recent]);

  /* ─────────────────────────────────────
     RENDER
  ───────────────────────────────────── */
  return (
    <div
      aria-modal="true"
      role="dialog"
      aria-label="Búsqueda global"
      className={`
        fixed inset-0 z-[200]
        transition-opacity duration-200 ease-out
        ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
      `}
    >
      {/* BACKDROP — oscurecido muy leve, SIN blur: la página se ve nítida detrás */}
      <div className="absolute inset-0 bg-slate-900/20" onClick={onClose} />

      {/* PANEL FLOTANTE — alineado arriba, ancho acotado, SIN fondo sólido */}
      <div
        className={`
          absolute inset-x-0 top-0 max-w-screen-sm mx-auto px-3 sm:px-4
          flex flex-col
          transition-transform duration-200 ease-out
          ${open ? "translate-y-0" : "-translate-y-2"}
        `}
        style={{ paddingTop: "max(env(safe-area-inset-top), 8vh)" }}
      >

        {/* ═══ BARRA flotante ═══ */}
        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="shrink-0 bg-white dark:bg-slate-800 rounded-[20px] border border-slate-200/60 dark:border-slate-700/60 shadow-[0_8px_30px_rgba(11,31,58,0.18)] dark:shadow-none"
        >
          {/* INPUT ROW */}
          <div className="flex items-center gap-3 px-4 h-[54px]">
            <Search size={18} strokeWidth={2.2} className="text-[#1A7FFF] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder="Buscar personas, hogares, oraciones..."
              className="flex-1 min-w-0 bg-transparent text-[16px] text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none"
            />
            {localQuery.length > 0 && (
              <button
                onClick={() => setLocalQuery("")}
                aria-label="Limpiar búsqueda"
                className="shrink-0 h-7 w-7 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors active:scale-90"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            )}
            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />
            <button
              onClick={onClose}
              aria-label="Cerrar búsqueda"
              className="shrink-0 h-8 w-8 flex items-center justify-center rounded-full text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors active:scale-95"
            >
              <X size={17} strokeWidth={2.5} />
            </button>
          </div>

          {/* INDEX LOADING INDICATOR */}
          {indexState === "loading" && localReady && (
            <div className="flex items-center gap-1.5 px-4 pb-2.5">
              <div className="h-1.5 w-1.5 rounded-full bg-[#1A7FFF] animate-pulse" />
              <span className="text-[10px] text-slate-400">Indexando datos...</span>
            </div>
          )}

          {/* QUICK ACTIONS — visible when no query (<3 caracteres) */}
          {!localReady && (
            <div
              className="flex gap-2 px-4 pb-3 overflow-x-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}
            >
              {QUICK_ACTIONS.map((action) => {
                const QIcon = action.Icon;
                return (
                  <button
                    key={action.route}
                    onClick={() => handleNavigate(action.route, null)}
                    className="flex items-center gap-1.5 px-3 h-8 rounded-full shrink-0 bg-slate-100/80 dark:bg-slate-700 border border-slate-200/70 dark:border-slate-600 text-[12px] font-medium text-slate-600 dark:text-slate-300 transition-all duration-150 hover:bg-slate-200/60 active:scale-[0.96]"
                  >
                    <QIcon size={12} strokeWidth={2} className="text-slate-500" />
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* FILTER TABS — visible when query (>=3) + multiple section types */}
          {localReady && showTabs && (
            <div
              className="flex gap-1.5 px-4 pb-3 overflow-x-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}
            >
              {computedTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSelectedIndex(-1); }}
                  className={`
                    flex items-center gap-1.5 px-3 h-7 rounded-full shrink-0
                    text-[11px] font-semibold
                    transition-all duration-150
                    ${activeTab === tab.id
                      ? "bg-[#1A7FFF] text-white shadow-sm shadow-blue-200"
                      : "bg-white dark:bg-slate-700 border border-slate-200/80 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-blue-300 hover:text-blue-600"
                    }
                  `}
                >
                  {tab.label}
                  <span className={`text-[9px] font-bold leading-none ${activeTab === tab.id ? "text-blue-100" : "text-slate-400"}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ═══ TARJETA de resultados — flotante, solo con ≥3 letras (como el inline) ═══ */}
        {qReady && (
        <div
          className="anim-in mt-2.5 bg-[#f0f5fb] dark:bg-slate-800 rounded-[20px] border border-slate-200/50 dark:border-slate-700/50 shadow-[0_8px_30px_rgba(11,31,58,0.15)] dark:shadow-none overflow-hidden"
        >
          <div
            ref={scrollRef}
            className="max-h-[60vh] overflow-y-auto overscroll-contain"
            style={{
              WebkitOverflowScrolling: "touch",
              paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
            }}
          >

          {/* LOADING */}
          {loading && (
            <div className="flex flex-col items-center justify-center pt-16 pb-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1A7FFF] border-t-transparent" />
              <p className="mt-3 text-[13px] text-slate-400">Cargando datos...</p>
            </div>
          )}

          {/* ─────────────────────────────
              EMPTY STATE — no query (<3)
          ───────────────────────────── */}
          {!loading && !qReady && (
            <div className="px-4 pt-4 space-y-4">

              {/* RECENTS — grouped by date */}
              {recentGroups.length > 0 && (
                <div className="space-y-3">
                  {recentGroups.map(([label, items]) => (
                    <div key={label}>
                      <SectionLabel label={label} />
                      <ResultCard>
                        {items.map((item, i) => (
                          <ResultRow
                            key={`${item.type}-${item.id}`}
                            Icon={Clock3}
                            iconBg="bg-slate-100"
                            iconColor="text-slate-500"
                            title={item.title}
                            subtitle={item.subtitle}
                            query=""
                            isLast={i === items.length - 1}
                            isSelected={false}
                            index={-1}
                            itemRefs={itemRefs}
                            onHover={() => {}}
                            onClick={() => handleNavigate(item.route, null)}
                          />
                        ))}
                      </ResultCard>
                    </div>
                  ))}
                </div>
              )}

              {/* HINT CARD */}
              <div className="bg-white dark:bg-slate-800 rounded-[20px] border border-slate-100 dark:border-slate-700/50 shadow-sm overflow-hidden">
                <div className="p-5">
                  <p className="text-[14px] font-semibold text-slate-800 dark:text-slate-100">Búsqueda global</p>
                  <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">
                    Busca personas, hogares, iglesias, visitas, oraciones y más.
                  </p>
                </div>
                <div className="px-5 pb-4 flex flex-wrap gap-2">
                  {["Iglesias", "Hogares", "Personas", "Visitas", "Oraciones"].map((hint) => (
                    <button
                      key={hint}
                      onClick={() => setLocalQuery(hint.toLowerCase())}
                      className="px-3 py-1 rounded-full bg-slate-100 text-[11px] font-medium text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      {hint}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* ─────────────────────────────
              RESULTS — query active (>=3)
          ───────────────────────────── */}
          {!loading && qReady && (
            <SearchResults
              showCommands={showCommands}
              matchedCommands={matchedCommands}
              visibleSections={visibleSections}
              query={query}
              selectedIndex={selectedIndex}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              itemRefs={itemRefs}
              onHover={setSelectedIndex}
              onNavigate={handleNavigate}
              onPickHint={setLocalQuery}
              hasResults={hasResults}
              totalResults={totalResults}
              stickyBg="bg-[#f0f5fb] dark:bg-slate-800"
            />
          )}

          </div>
        </div>
        )}
      </div>
    </div>
  );
}
